// src/pages/ttc/TTCMyJourney.jsx
// Route: /demo/ttc/myjourney
// Persona: Maya, cycle day 14, fertile window, trying for 4 months naturally.

import { useState } from "react";
import SectionHeader from "../../components/SectionHeader";
import { demoChapterStackColumnStyle, DEMO_SECTION_STACK_GAP_PX } from "../../designSystem";

const C = {
  primary: "var(--terracotta)", primaryL: "var(--terracotta-l)", primaryM: "var(--terracotta-m)", primaryDk: "var(--terracotta-d)",
  sand: "var(--sand)", sand2: "var(--sand-2)", sand3: "var(--sand-3)", white: "var(--white)", card: "var(--card)", border: "var(--border)",
  muted: "var(--muted)", text: "var(--text)", text2: "var(--text-2)",
  sage: "var(--sage)", sageL: "var(--sage-l)", sageM: "var(--sage-m)", sageD: "var(--sage-d)",
  ppL: "var(--purple-l)", ppM: "var(--purple-m)", ppD: "var(--purple-d)",
  amL: "var(--amber-l)", am: "var(--amber)", r: "var(--r)",
};

const s = {
  card: { background: C.white, border: `1px solid ${C.border}`, borderRadius: C.r, padding: 16, marginBottom: 10 },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${C.sand2}`, fontSize: 12 },
};

const Btn = ({ children, variant = "primary", onClick, style = {} }) => {
  const vs = {
    primary: { background: C.primary, color: "#fff", border: "none" },
    ghost:   { background: C.primaryL, color: C.primaryDk, border: `1px solid ${C.primaryM}` },
    sand:    { background: C.sand, color: C.muted, border: `1px solid ${C.border}` },
  };
  return <button onClick={onClick} style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 700, borderRadius: 999, padding: "7px 16px", cursor: "pointer", ...vs[variant], ...style }}>{children}</button>;
};

const Tag = ({ children, bg, color, border }) => (
  <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 9px", borderRadius: 999, background: bg, color, border: `1px solid ${border}`, display: "inline-block" }}>{children}</span>
);

const MOODS = ["😩","😢","😞","😐","😑","🙂","😊","😄","🤩"];
const MOOD_LABELS = ["Rough","Sad","Low","Okay","Meh","Good","Great","Happy","Amazing"];
const SYMPTOMS = ["Cramping","Bloating","Spotting","Breast tenderness","Headache","Fatigue","Mood swings","Nausea","Hot flash","EWCM","Positive OPK","Temp rise"];

export default function TTCMyJourney({ navigate }) {
  const [mood, setMood] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [activeSection, setActiveSection] = useState("tracker");
  const nav = navigate || (() => {});

  const toggleSymptom = (s) => setSymptoms(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif", ...demoChapterStackColumnStyle, padding: `0 0 ${DEMO_SECTION_STACK_GAP_PX}px` }}>

        {/* Stats strip */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          {[
            { label: "Cycle day", value: "14", sub: "Fertile window" },
            { label: "Days trying", value: "4 mo", sub: "Since Nov 2025" },
            { label: "TWW countdown", value: "—", sub: "Not yet started" },
          ].map(s => (
            <div key={s.label} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--r)", padding: 16 }}>
              <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: "-.02em" }}>{s.value}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Section tabs */}
        <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${C.border}`, background: C.white }}>
          {["tracker","cycle","opk","tww","meds","reminders"].map(tab => (
            <button key={tab} onClick={() => setActiveSection(tab)} style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: activeSection === tab ? C.primaryDk : C.muted, background: "none", border: "none", borderBottom: `2px solid ${activeSection === tab ? C.primary : "transparent"}`, padding: "10px 14px", cursor: "pointer", whiteSpace: "nowrap", textTransform: "capitalize" }}>
              {tab === "opk" ? "OPK log" : tab === "tww" ? "TWW" : tab === "meds" ? "Supplements" : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* ── TRACKER TAB ── */}
        {activeSection === "tracker" && (
          <div>
            {/* Fertile window calendar */}
            <div style={{ marginBottom: 14 }}>
              <SectionHeader title="Your cycle this month" descriptor="Peak days and your fertile window at a glance." />
            </div>
            <div style={{ ...s.card }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4, marginBottom: 12 }}>
                {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
                  <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: C.muted, paddingBottom: 4 }}>{d}</div>
                ))}
                {Array.from({length: 31}, (_, i) => i + 1).map(day => {
                  const isFertile = day >= 11 && day <= 15;
                  const isPeak = day === 14;
                  const isToday = day === 23;
                  const isPast = day < 23;
                  const isLuteul = day >= 15 && day <= 28;
                  return (
                    <div key={day} style={{
                      textAlign: "center", borderRadius: 8, padding: "6px 2px", fontSize: 12, fontWeight: isPeak || isToday ? 700 : 400,
                      background: isPeak ? C.primary : isFertile ? C.primaryL : isToday ? C.sand2 : "transparent",
                      color: isPeak ? "#fff" : isFertile ? C.primaryDk : isToday ? C.text : isPast ? C.text2 : C.muted,
                      border: isToday && !isPeak ? `1px solid ${C.border}` : "1px solid transparent",
                      cursor: "pointer",
                    }}>
                      {day}
                      {isPeak && <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#fff", margin: "2px auto 0" }} />}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11 }}><div style={{ width: 12, height: 12, borderRadius: 3, background: C.primary }} /><span style={{ color: C.muted }}>Peak fertility (day 14)</span></div>
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11 }}><div style={{ width: 12, height: 12, borderRadius: 3, background: C.primaryL }} /><span style={{ color: C.muted }}>Fertile window (days 11–15)</span></div>
              </div>
            </div>

            {/* Today's log */}
            <div style={{ marginBottom: 14 }}>
              <SectionHeader
                title="Today's log"
                descriptor="How are you feeling and what happened today."
              />
            </div>
            <div style={{ ...s.card }}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <span style={{ fontSize: "12px", color: "rgba(80,40,60,0.55)" }}>
                  {new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                </span>
              </div>

              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 }}>1. How are you feeling?</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {MOODS.map((emoji, i) => (
                    <button key={i} onClick={() => setMood(i)} style={{ background: mood === i ? C.primaryL : C.sand, border: `1px solid ${mood === i ? C.primaryM : C.border}`, borderRadius: 10, padding: "8px 10px", cursor: "pointer", textAlign: "center", minWidth: 52 }}>
                      <div style={{ fontSize: 20 }}>{emoji}</div>
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{MOOD_LABELS[i]}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 }}>2. Symptoms & observations</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {SYMPTOMS.map(sym => (
                    <button key={sym} onClick={() => toggleSymptom(sym)} style={{ fontSize: 11, fontWeight: 600, padding: "5px 11px", borderRadius: 999, border: `1px solid ${symptoms.includes(sym) ? C.primaryM : C.border}`, background: symptoms.includes(sym) ? C.primaryL : C.sand, color: symptoms.includes(sym) ? C.primaryDk : C.muted, cursor: "pointer" }}>
                      {sym}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 }}>3. BBT this morning (optional)</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="text" placeholder="e.g. 97.8 °F" style={{ fontFamily: "inherit", fontSize: 13, padding: "8px 12px", borderRadius: 999, border: `1px solid ${C.border}`, background: C.sand, color: C.text, width: 140 }} />
                  <div style={{ fontSize: 11, color: C.muted }}>A temp rise of 0.2°F+ confirms ovulation</div>
                </div>
              </div>

              <Btn variant="primary" style={{ width: "100%", padding: "10px 16px", fontSize: 13 }}>Save today's log</Btn>
            </div>
          </div>
        )}

        {/* ── CYCLE TAB ── */}
        {activeSection === "cycle" && (
          <div>
            <div style={{ marginBottom: 14 }}>
              <SectionHeader title="Your cycle story" descriptor="Trying timeline and cycle details you've logged." />
            </div>
            <div style={{ ...s.card }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                {[
                  ["Trying since", "Nov 2025"], ["Cycle length", "28 days (avg)"],
                  ["Period length", "5 days"],    ["Cycle regularity", "Regular"],
                  ["Diagnosis", "Not added"],     ["Partner factor", "Not added"],
                  ["Last period", "Mar 9, 2026"], ["Next period", "~Apr 6, 2026"],
                ].map(([k,v]) => (
                  <div key={k} style={{ background: C.sand, borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: v === "Not added" ? C.muted : C.text }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: C.primaryL, border: `1px solid ${C.primaryM}`, borderRadius: C.r, padding: "10px 14px", fontSize: 12, color: C.primaryDk, lineHeight: 1.5 }}>
                <strong>Nova's note:</strong> At 4 months of trying with regular cycles, you're still in the normal range. Most healthy couples conceive within 12 months. Nova is tracking your patterns — if anything looks unusual, she'll flag it.
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <SectionHeader title="Cycle history" descriptor="Past cycles, length, and peak days." />
            </div>
            <div style={{ ...s.card }}>
              {[
                { month: "Feb 2026", cd: "28 days", peak: "Day 13", conceived: false },
                { month: "Jan 2026", cd: "29 days", peak: "Day 14", conceived: false },
                { month: "Dec 2025", cd: "27 days", peak: "Day 12", conceived: false },
                { month: "Nov 2025", cd: "28 days", peak: "Day 14", conceived: false, first: true },
              ].map((c, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.sand2}` }}>
                  <div style={{ width: 48, flexShrink: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{c.month}</div>
                    {c.first && <div style={{ fontSize: 10, color: C.primary, marginTop: 1 }}>First month</div>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: C.text2 }}>{c.cd} · Peak: {c.peak}</div>
                  </div>
                  <Tag bg={C.sand2} color={C.muted} border={C.border}>Not conceived</Tag>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── OPK TAB ── */}
        {activeSection === "opk" && (
          <div>
            <div style={{ marginBottom: 14 }}>
              <SectionHeader title="OPK log — this cycle" descriptor="Your LH tests and line progression this cycle." />
            </div>
            <div style={{ ...s.card }}>
              <div style={{ background: C.primaryL, border: `1px solid ${C.primaryM}`, borderRadius: C.r, padding: "12px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.primaryDk, marginBottom: 4 }}>How to read your OPK</div>
                <div style={{ fontSize: 12, color: C.primaryDk, lineHeight: 1.55 }}>A positive OPK means the test line is as dark or darker than the control line. Ovulation typically happens 24–36 hours after your LH surge. Log your result daily from day 10 onwards.</div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 10 }}>Log today's OPK result</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {["Negative", "Almost positive", "Positive ⚡", "Peak (flashing)"].map(r => (
                    <button key={r} style={{ fontFamily: "inherit", fontSize: 11, fontWeight: 600, padding: "8px 14px", borderRadius: C.r, border: `1px solid ${r.includes("Positive") ? C.primaryM : C.border}`, background: r.includes("Positive") ? C.primaryL : C.sand, color: r.includes("Positive") ? C.primaryDk : C.muted, cursor: "pointer" }}>{r}</button>
                  ))}
                </div>
              </div>

              {/* OPK history this cycle */}
              {[
                { day: "CD 10", date: "Mar 19", result: "Negative",  line: 0.2 },
                { day: "CD 11", date: "Mar 20", result: "Negative",  line: 0.35 },
                { day: "CD 12", date: "Mar 21", result: "Low",       line: 0.5 },
                { day: "CD 13", date: "Mar 22", result: "Almost +",  line: 0.75 },
                { day: "CD 14", date: "Mar 23", result: "Log now",   line: null, today: true },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.sand2}` }}>
                  <div style={{ width: 40, flexShrink: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: r.today ? C.primary : C.text }}>{r.day}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{r.date}</div>
                  </div>
                  <div style={{ flex: 1, height: 8, borderRadius: 999, background: C.sand2, overflow: "hidden" }}>
                    {r.line && <div style={{ width: `${r.line * 100}%`, height: "100%", background: r.line >= 0.8 ? C.primary : r.line >= 0.6 ? C.primaryM : C.sand3, borderRadius: 999 }} />}
                  </div>
                  <div style={{ width: 80, textAlign: "right", fontSize: 11, fontWeight: 600, color: r.today ? C.primary : r.result === "Almost +" ? C.am : C.muted }}>{r.result}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── TWW TAB ── */}
        {activeSection === "tww" && (
          <div>
            <div style={{ marginBottom: 14 }}>
              <SectionHeader title="Two-week wait" descriptor="Countdown and daily check-ins after ovulation." />
            </div>
            <div style={{ ...s.card }}>
              <div style={{ background: C.sand, borderRadius: C.r, padding: "20px", textAlign: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>💜</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>Your TWW hasn't started yet</div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, maxWidth: 400, margin: "0 auto 14px" }}>Once you confirm ovulation (via a temp rise or positive OPK), Nova will start your TWW countdown and check in with you every day.</div>
                <Btn variant="ghost">I've confirmed ovulation</Btn>
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 10 }}>What to expect in the TWW</div>
              {[
                { dpo: "1–5 DPO", title: "Implantation window", body: "If fertilization happened, the embryo is traveling to the uterus. No symptoms yet — anything you feel is likely progesterone." },
                { dpo: "6–10 DPO", title: "Possible implantation", body: "Some women feel light cramping or spotting (implantation). Most feel nothing. Progesterone can cause breast tenderness, fatigue, and mood changes regardless of outcome." },
                { dpo: "11–14 DPO", title: "Test time", body: "HCG is detectable from ~10–12 DPO. A negative at 10 DPO is not final. Wait until 12–14 DPO for the most accurate result." },
              ].map((item, i) => (
                <div key={i} style={{ padding: "10px 0", borderBottom: `1px solid ${C.sand2}` }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <Tag bg={C.primaryL} color={C.primaryDk} border={C.primaryM}>{item.dpo}</Tag>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 3 }}>{item.title}</div>
                      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{item.body}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── SUPPLEMENTS TAB ── */}
        {activeSection === "meds" && (
          <div>
            <div style={{ marginBottom: 14 }}>
              <SectionHeader
                title="Supplement protocol"
                descriptor="Add each supplement with when to take it. You'll get reminders at the times you set."
              />
            </div>
            <div style={{ ...s.card }}>
              <div style={{ marginBottom: 14 }}>
                {[
                  { name: "Prenatal vitamin", dose: "1 capsule", timing: "Daily with food", reminder: "8:00 AM" },
                  { name: "Folate (400mcg)", dose: "400mcg",    timing: "Daily",           reminder: "8:00 AM" },
                  { name: "CoQ10",           dose: "400mg",     timing: "Daily",           reminder: "12:00 PM" },
                ].map((med, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: C.sand, borderRadius: C.r, marginBottom: 8 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.primaryL, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>💊</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{med.name}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{med.dose} · {med.timing}</div>
                      <div style={{ fontSize: 11, color: C.primary, marginTop: 2 }}>🔔 Reminder: {med.reminder}</div>
                    </div>
                    <button style={{ fontFamily: "inherit", fontSize: 11, color: C.muted, background: "none", border: `1px solid ${C.border}`, borderRadius: 999, padding: "3px 10px", cursor: "pointer" }}>Remove</button>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input placeholder="Add supplement..." style={{ fontFamily: "inherit", fontSize: 12, padding: "8px 12px", borderRadius: 999, border: `1px solid ${C.border}`, background: C.sand, flex: 1 }} />
                <Btn variant="primary">Add</Btn>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <SectionHeader title="Nova recommends for TTC" descriptor="Evidence-informed options to discuss with your doctor." />
            </div>
            <div style={{ ...s.card }}>
              {[
                { name: "Prenatal with folate", why: "Critical before and during early pregnancy — start 3 months before conceiving if possible" },
                { name: "CoQ10 (200–600mg)", why: "Supports egg quality. Particularly useful if you're over 35 or have been trying for several months" },
                { name: "Vitamin D", why: "Low levels linked to longer time to conceive. Ask your doctor to check your levels" },
                { name: "Omega-3 (DHA)", why: "Supports egg development and early fetal brain development" },
              ].map((r, i) => (
                <div key={i} style={{ padding: "8px 0", borderBottom: `1px solid ${C.sand2}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{r.name}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2, lineHeight: 1.45 }}>{r.why}</div>
                </div>
              ))}
              <div style={{ fontSize: 11, color: C.muted, marginTop: 10, fontStyle: "italic" }}>Always check with your doctor before starting new supplements.</div>
            </div>
          </div>
        )}

        {/* ── REMINDERS TAB ── */}
        {activeSection === "reminders" && (
          <div>
            <div style={{ marginBottom: 14 }}>
              <SectionHeader title="Reminders" descriptor="OPK, BBT, and supplement reminder times." />
            </div>
            <div style={{ ...s.card }}>
              <div style={{ background: C.primaryL, border: `1px solid ${C.primaryM}`, borderRadius: C.r, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: C.primaryDk, lineHeight: 1.5 }}>
                OPK testing reminder: <strong>Test daily from cycle day 10</strong>, preferably between 10am–2pm, using second morning urine. You're on day 14 — check today!
              </div>
              {[
                { type: "OPK test",        time: "12:00 PM",  days: "CD 10–16", active: true },
                { type: "BBT temp",        time: "7:00 AM",   days: "Daily",    active: true },
                { type: "Prenatal vitamin",time: "8:00 AM",   days: "Daily",    active: true },
                { type: "CoQ10",           time: "12:00 PM",  days: "Daily",    active: true },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: C.sand, borderRadius: C.r, marginBottom: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: r.active ? "#22c55e" : C.sand2, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{r.type}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{r.time} · {r.days}</div>
                  </div>
                  <button style={{ fontFamily: "inherit", fontSize: 11, color: C.muted, background: "none", border: `1px solid ${C.border}`, borderRadius: 999, padding: "3px 10px", cursor: "pointer" }}>Edit</button>
                </div>
              ))}
              <Btn variant="ghost" style={{ width: "100%", marginTop: 4 }}>+ Add reminder</Btn>
            </div>

            <div style={{ marginBottom: 14 }}>
              <SectionHeader title="When to see a doctor" descriptor="Nova recommends booking a fertility workup if:" />
            </div>
            <div style={{ ...s.card }}>
              {[
                "You've been trying for 12 months (6 months if you're over 35)",
                "Your cycles are irregular, very short, or very long",
                "You have a history of PCOS, endometriosis, or thyroid conditions",
                "Your partner has never had a fertility check",
                "You've had two or more pregnancy losses",
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 8, padding: "6px 0", fontSize: 12, color: C.text2, alignItems: "baseline" }}>
                  <span style={{ color: C.primary, fontWeight: 700, flexShrink: 0 }}>·</span>
                  {item}
                </div>
              ))}
              <Btn variant="primary" onClick={() => nav("/signup")} style={{ marginTop: 14, width: "100%" }}>Find a fertility specialist →</Btn>
            </div>
          </div>
        )}
    </div>
  );
}
